/**
 * @file SeasonSelectDropdownContainer — season selector for the player stats table.
 * @description SE-5570: the season dropdown migrated off the retired gluestack
 * Menu atom onto the shared `SelectDropdown` molecule
 * (FixedPositionDropdownShell). It owns its own open state so the inline stats
 * table header and the "View all" overlay each render an independent instance.
 * When opened from inside the "View all" overlay/Modal the shell mounts its
 * panel in its own RN Modal stacked above that overlay (on web it portals to
 * the document body) — this stacked-Modal behavior is intentional.
 * @module features/player-detail/components/molecules/SeasonSelectDropdown
 */
import { ChevronDown } from "lucide-react-native";
import React, { memo, useCallback, useState } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

import type { SeasonOption } from "@/features/player-detail/types";

/** Props for {@link SeasonSelectDropdownContainer}. */
export interface SeasonSelectDropdownProps {
  /** Available season options */
  readonly seasonOptions: readonly SeasonOption[];
  /** Currently selected season ID */
  readonly selectedSeason: string;
  /** Display label for the selected season (trigger text) */
  readonly selectedSeasonLabel: string;
  /** Invoked with the chosen season ID */
  readonly onSelectSeason: (seasonId: string) => void;
}

/**
 * Season selector rendered through the shared SelectDropdown molecule.
 * @param props - Component props
 * @param props.seasonOptions - Available season options
 * @param props.selectedSeason - Currently selected season ID
 * @param props.selectedSeasonLabel - Display label for the selected season
 * @param props.onSelectSeason - Invoked with the chosen season ID
 * @returns The season SelectDropdown element
 */
const SeasonSelectDropdownContainer = ({
  seasonOptions,
  selectedSeason,
  selectedSeasonLabel,
  onSelectSeason,
}: SeasonSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);
  const onClose = useCallback(() => setIsOpen(false), []);

  const options: SelectDropdownOption[] = seasonOptions.map(option => ({
    id: option.id,
    label: option.label,
    selected: selectedSeason === option.id,
    onPress: () => onSelectSeason(option.id),
  }));

  return (
    <SelectDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      panelClassName="max-w-56"
      triggerAccessibilityLabel="Select season"
      triggerContent={
        <Stack direction="horizontal" className="items-center gap-1">
          <Text variant="caption" className="text-content-muted">
            {selectedSeasonLabel}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-muted" />
        </Stack>
      }
      options={options}
    />
  );
};

SeasonSelectDropdownContainer.displayName = "SeasonSelectDropdownContainer";

export default memo(SeasonSelectDropdownContainer);
